import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Phone, Mail, Wallet, Calendar as CalendarIcon,
  CheckCircle2, Plus, Trash2, Receipt, Award, Banknote, RotateCcw, Loader2, FileText,
  FileDown, FileSpreadsheet, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNTS = [
  { id: 'nakit', label: 'Nakit' },
  { id: 'garanti_bbva', label: 'Garanti BBVA' },
  { id: 'bostanci', label: 'Bostancı' }
];

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const monthLabel = (m) => {
  if (!m || m.length < 7) return m || '-';
  try {
    return new Date(`${m}-01T00:00:00`).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  } catch { return m; }
};
const accountLabel = (id) => ACCOUNTS.find(a => a.id === id)?.label || (id === 'enpara' ? 'Enpara' : id || '-');
const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const CustomerStatementModal = ({ customerId, open, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('reservations');

  // Rezervasyon işlem paneli
  const [selectedRes, setSelectedRes] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newExpense, setNewExpense] = useState({ amount: '', description: '' });
  const [payMethod, setPayMethod] = useState('nakit');

  // Kısmi tahsilat
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectForm, setCollectForm] = useState({ amount: '', payment_method: 'nakit', date: new Date().toISOString().split('T')[0], notes: '' });

  // Ekstre çıktısı / WhatsApp
  const [exporting, setExporting] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);

  const loadStatement = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/statement`);
      setData(res.data);
      setSelectedRes(prev => prev ? (res.data.reservations || []).find(r => r.id === prev.id) || null : null);
    } catch (e) {
      toast.error('Müşteri cari bilgisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) loadStatement();
    if (!open) { setSelectedRes(null); setActionOpen(false); }
  }, [open, customerId, loadStatement]);

  const grouped = useMemo(() => {
    const groups = {};
    (data?.reservations || []).forEach(r => {
      const key = (r.date || '').slice(0, 7) || '-';
      groups[key] = groups[key] || [];
      groups[key].push(r);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data]);

  const afterAction = async () => {
    await loadStatement();
    onRefresh?.();
  };

  const openResAction = (r) => {
    setSelectedRes(r);
    setPayMethod(r.payment_method && ACCOUNTS.some(a => a.id === r.payment_method) ? r.payment_method : 'nakit');
    setNewExpense({ amount: '', description: '' });
    setActionOpen(true);
  };

  const changePaymentStatus = async (status) => {
    if (!selectedRes) return;
    setBusy(true);
    try {
      await axios.put(`${API}/reservations/${selectedRes.id}/payment-status`, {
        payment_status: status,
        payment_method: payMethod
      });
      toast.success(status === 'odendi' ? 'Ödeme alındı olarak işlendi' : 'Ödeme geri alındı');
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally { setBusy(false); }
  };

  const toggleInvoice = async () => {
    if (!selectedRes) return;
    setBusy(true);
    try {
      await axios.put(`${API}/reservations/${selectedRes.id}/invoice-status`, { invoice_issued: !selectedRes.invoice_issued });
      toast.success(!selectedRes.invoice_issued ? 'Fatura kesildi olarak işaretlendi' : 'Fatura işareti kaldırıldı');
      await afterAction();
    } catch (e) {
      toast.error('Fatura durumu güncellenemedi');
    } finally { setBusy(false); }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    if (!selectedRes) return;
    const amount = parseFloat(newExpense.amount);
    if (!amount || amount <= 0) { toast.error('Geçerli bir tutar girin'); return; }
    if (!newExpense.description || newExpense.description.trim().length < 3) { toast.error('Açıklama en az 3 karakter olmalı'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/reservation-expenses`, {
        reservation_id: selectedRes.id,
        amount,
        description: newExpense.description.trim(),
        date: selectedRes.date
      });
      toast.success('Gider eklendi');
      setNewExpense({ amount: '', description: '' });
      await afterAction();
    } catch (e) {
      toast.error('Gider eklenemedi');
    } finally { setBusy(false); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Bu gideri silmek istiyor musunuz?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/reservation-expenses/${id}`);
      toast.success('Gider silindi');
      await afterAction();
    } catch (e) {
      toast.error('Gider silinemedi');
    } finally { setBusy(false); }
  };

  const downloadStatement = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/statement/export`, {
        params: { format }, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (c?.name || 'musteri').replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
      a.download = `cari_ekstre_${safe}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF ekstre indirildi' : 'Excel ekstre indirildi');
    } catch (e) {
      toast.error('Ekstre oluşturulamadı');
    } finally { setExporting(false); }
  };

  const sendWhatsapp = async () => {
    if (!c?.phone) { toast.error('Müşterinin telefon numarası kayıtlı değil'); return; }
    if (!window.confirm(`${c.name} (${c.phone}) numarasına cari ekstre linki WhatsApp ile gönderilsin mi?`)) return;
    setSendingWa(true);
    try {
      const res = await axios.post(`${API}/customers/${customerId}/statement/whatsapp`, {});
      if (res.data?.sent) toast.success(`Ekstre WhatsApp ile gönderildi: ${res.data.phone}`);
      else toast.warning('WhatsApp gönderimi başarısız oldu, link oluşturuldu');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'WhatsApp gönderilemedi');
    } finally { setSendingWa(false); }
  };

  const submitCollect = async (e) => {    e.preventDefault();
    const amount = parseFloat(collectForm.amount);
    if (!amount || amount <= 0) { toast.error('Geçerli bir tutar girin'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/customers/${customerId}/payment`, {
        amount,
        payment_method: collectForm.payment_method,
        date: collectForm.date,
        notes: collectForm.notes || null
      });
      toast.success(`${formatTL(amount)} tahsilat ${accountLabel(collectForm.payment_method)} kasasına işlendi`);
      setCollectOpen(false);
      setCollectForm({ amount: '', payment_method: 'nakit', date: new Date().toISOString().split('T')[0], notes: '' });
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tahsilat kaydedilemedi');
    } finally { setBusy(false); }
  };

  const c = data?.customer;
  const kpi = data?.kpi;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="customer-statement-modal">
          {loading && !data ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">Cari Hesap</DialogTitle>
              </DialogHeader>
              <div className="py-16 text-center text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cari hesap yükleniyor...
              </div>
            </>
          ) : c ? (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg shadow ${
                    (c.balance || 0) > 0 ? 'bg-gradient-to-br from-rose-500 to-red-600' : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                  }`}>{initials(c.name)}</div>
                  <div className="flex-1">
                    <DialogTitle className="text-xl" data-testid="statement-customer-name">{c.name}</DialogTitle>
                    <DialogDescription className="flex flex-wrap items-center gap-3 text-xs mt-1">
                      {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                      {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      <span>{kpi.reservation_count} rezervasyon (tüm aylar)</span>
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" disabled={exporting} onClick={() => downloadStatement('pdf')} data-testid="statement-pdf-btn">
                      <FileDown className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" disabled={exporting} onClick={() => downloadStatement('excel')} data-testid="statement-excel-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                    </Button>
                    <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={sendingWa} onClick={sendWhatsapp} data-testid="statement-whatsapp-btn">
                      {sendingWa ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> WhatsApp</>}
                    </Button>
                    <Button size="sm" onClick={() => setCollectOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-collect-payment-btn">
                      <Banknote className="w-4 h-4 mr-1" /> Tahsilat Al
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <Card className={`border-l-4 ${(kpi.balance || 0) > 0 ? 'border-l-rose-500 bg-rose-50' : 'border-l-emerald-500 bg-emerald-50'}`} data-testid="statement-kpi-balance">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Cari Bakiye</p>
                    <p className={`text-lg font-bold ${(kpi.balance || 0) > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatTL(Math.abs(kpi.balance || 0))}</p>
                    <p className="text-[10px] text-slate-500">{(kpi.balance || 0) > 0 ? 'borçlu' : (kpi.balance || 0) < 0 ? 'alacaklı' : 'tamam'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500 bg-blue-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Toplam Ciro</p>
                    <p className="text-lg font-bold text-blue-700">{formatTL(kpi.total_amount)}</p>
                    <p className="text-[10px] text-slate-500">{kpi.reservation_count} maç</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500 bg-emerald-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Kasaya Giren</p>
                    <p className="text-lg font-bold text-emerald-700">{formatTL(kpi.collected_in_kasa)}</p>
                    <p className="text-[10px] text-slate-500">kapora + tahsilat</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500 bg-amber-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Bekleyen</p>
                    <p className="text-lg font-bold text-amber-700">{formatTL(kpi.pending_total)}</p>
                    <p className="text-[10px] text-slate-500">ödenmemiş maç tutarı</p>
                  </CardContent>
                </Card>
              </div>

              <Tabs value={tab} onValueChange={setTab} className="mt-2">
                <TabsList>
                  <TabsTrigger value="reservations" data-testid="statement-tab-reservations">Rezervasyonlar</TabsTrigger>
                  <TabsTrigger value="transactions" data-testid="statement-tab-transactions">Cari Hareketler</TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="statement-tab-monthly">Aylık Özet</TabsTrigger>
                </TabsList>

                <TabsContent value="reservations" className="space-y-3 mt-3">
                  {grouped.length === 0 ? (
                    <p className="text-center py-10 text-slate-400 text-sm">Rezervasyon bulunmuyor</p>
                  ) : grouped.map(([month, list]) => (
                    <Card key={month}>
                      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b rounded-t-lg">
                        <p className="text-sm font-semibold text-slate-700">{monthLabel(month)}</p>
                        <p className="text-xs text-slate-500">{list.length} maç • {formatTL(list.reduce((s, r) => s + (r.total_amount || 0), 0))}</p>
                      </div>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-white border-b">
                              <tr className="text-left text-slate-500">
                                <th className="px-3 py-2 font-medium">Tarih</th>
                                <th className="px-2 py-2 font-medium">Salon</th>
                                <th className="px-2 py-2 font-medium">Hakem</th>
                                <th className="px-2 py-2 font-medium text-right">Tutar</th>
                                <th className="px-2 py-2 font-medium text-right">Kapora</th>
                                <th className="px-2 py-2 font-medium text-right">Gider</th>
                                <th className="px-2 py-2 font-medium text-center">Durum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {list.map(r => (
                                <tr
                                  key={r.id}
                                  className="border-b hover:bg-blue-50/60 cursor-pointer transition-colors"
                                  onClick={() => openResAction(r)}
                                  data-testid={`statement-res-row-${r.id}`}
                                >
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="font-medium text-slate-800">{formatDate(r.date)}</div>
                                    <div className="text-[10px] text-slate-500">{r.start_time?.slice(0, 5)}–{r.end_time?.slice(0, 5)}</div>
                                  </td>
                                  <td className="px-2 py-2">{r.salon_name}</td>
                                  <td className="px-2 py-2">{r.referee_name || <span className="text-slate-400">-</span>}</td>
                                  <td className="px-2 py-2 text-right font-mono">{formatTL(r.total_amount)}</td>
                                  <td className="px-2 py-2 text-right font-mono text-emerald-700">{r.paid_amount ? formatTL(r.paid_amount) : '-'}</td>
                                  <td className="px-2 py-2 text-right font-mono text-rose-600">{r.expenses_total ? formatTL(r.expenses_total) : '-'}</td>
                                  <td className="px-2 py-2 text-center">
                                    {r.paid_via_transfer ? (
                                      <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px]">Havale</Badge>
                                    ) : r.payment_status === 'odendi' ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{r.with_referee ? 'Hakeme Ödendi' : 'Ödendi'}</Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Bekliyor</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <p className="text-[11px] text-slate-400">İşlem yapmak için bir rezervasyona tıklayın (ödeme, gider, fatura).</p>
                </TabsContent>

                <TabsContent value="transactions" className="mt-3">
                  <Card>
                    <CardContent className="p-0">
                      {(data.transactions || []).length === 0 ? (
                        <p className="text-center py-10 text-slate-400 text-sm">Kasa hareketi yok</p>
                      ) : (
                        <div className="overflow-x-auto max-h-96">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50 sticky top-0 border-b">
                              <tr className="text-left text-slate-500">
                                <th className="px-3 py-2 font-medium">Tarih</th>
                                <th className="px-2 py-2 font-medium">Açıklama</th>
                                <th className="px-2 py-2 font-medium">Kasa</th>
                                <th className="px-2 py-2 font-medium text-right">Tutar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.transactions.map(t => (
                                <tr key={t.id} className="border-b hover:bg-slate-50" data-testid={`statement-tx-${t.id}`}>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                                  <td className="px-2 py-2">
                                    <div className="font-medium text-slate-700">{t.title}</div>
                                    {t.notes && <div className="text-[10px] text-slate-400 truncate max-w-[280px]">{t.notes}</div>}
                                  </td>
                                  <td className="px-2 py-2"><Badge variant="secondary" className="text-[10px]">{accountLabel(t.payment_method)}</Badge></td>
                                  <td className="px-2 py-2 text-right font-mono text-emerald-700">+{formatTL(t.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="monthly" className="mt-3">
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 border-b">
                            <tr className="text-left text-slate-500">
                              <th className="px-3 py-2 font-medium">Ay</th>
                              <th className="px-2 py-2 font-medium text-center">Maç</th>
                              <th className="px-2 py-2 font-medium text-right">Toplam</th>
                              <th className="px-2 py-2 font-medium text-right">Ödenen</th>
                              <th className="px-2 py-2 font-medium text-right">Bekleyen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.monthly || []).map(m => (
                              <tr key={m.month} className="border-b hover:bg-slate-50" data-testid={`statement-month-${m.month}`}>
                                <td className="px-3 py-2 font-medium text-slate-700">{monthLabel(m.month)}</td>
                                <td className="px-2 py-2 text-center">{m.count}</td>
                                <td className="px-2 py-2 text-right font-mono">{formatTL(m.total)}</td>
                                <td className="px-2 py-2 text-right font-mono text-emerald-700">{formatTL(m.paid)}</td>
                                <td className="px-2 py-2 text-right font-mono text-amber-700">{formatTL(m.pending)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ===== Rezervasyon İşlem Paneli ===== */}
      <Dialog open={actionOpen} onOpenChange={(v) => { if (!v) setActionOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="statement-res-action-modal">
          {selectedRes ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <CalendarIcon className="w-5 h-5 text-blue-600" />
                  {formatDate(selectedRes.date)} • {selectedRes.start_time?.slice(0, 5)}–{selectedRes.end_time?.slice(0, 5)}
                </DialogTitle>
                <DialogDescription className="flex flex-wrap gap-3 text-xs">
                  <span>{selectedRes.salon_name}</span>
                  {selectedRes.referee_name && <span className="flex items-center gap-1"><Award className="w-3 h-3" />{selectedRes.referee_name}</span>}
                  <span>{c?.name}</span>
                </DialogDescription>
              </DialogHeader>

              {/* Finansal özet */}
              <div className="rounded-lg border bg-slate-50 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Maç Tutarı</span><span className="font-mono font-semibold">{formatTL(selectedRes.total_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Alınan Kapora ({accountLabel(selectedRes.payment_method)})</span><span className="font-mono text-emerald-700">−{formatTL(selectedRes.paid_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Hakem Ücreti</span><span className="font-mono text-rose-600">−{formatTL(selectedRes.referee_fee)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Ekstra Giderler</span><span className="font-mono text-rose-600">−{formatTL(selectedRes.expenses_total)}</span></div>
                <div className="flex justify-between border-t pt-1.5">
                  <span className="font-semibold text-slate-700">{selectedRes.with_referee ? 'Hakemin Teslim Edeceği Net' : 'Net Tutar'}</span>
                  <span className="font-mono font-bold text-slate-900">{formatTL(selectedRes.net_amount)}</span>
                </div>
              </div>

              {/* Ödeme durumu */}
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Wallet className="w-4 h-4 text-emerald-600" /> Ödeme Durumu</p>
                {selectedRes.payment_status === 'odendi' ? (
                  <div className="flex items-center justify-between gap-2">
                    <Badge className="bg-emerald-100 text-emerald-800 border-0">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> {selectedRes.with_referee ? 'Hakeme Ödendi' : 'Ödendi'}
                    </Badge>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => changePaymentStatus('odenmedi')} data-testid="statement-unpay-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ödemeyi Geri Al
                    </Button>
                  </div>
                ) : (
                  <>
                    {!selectedRes.with_referee && (
                      <div>
                        <Label className="text-xs text-slate-500">Hangi Kasaya?</Label>
                        <div className="grid grid-cols-3 gap-1.5 mt-1">
                          {ACCOUNTS.map(a => (
                            <Button
                              key={a.id}
                              type="button"
                              size="sm"
                              variant={payMethod === a.id ? 'default' : 'outline'}
                              onClick={() => setPayMethod(a.id)}
                              className="text-xs"
                              data-testid={`statement-account-${a.id}`}
                            >{a.label}</Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedRes.with_referee && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        Hakemli maç: tutar kasaya otomatik yazılmaz. Hakemden "Tahsil Et" ile kasaya işlenir.
                      </p>
                    )}
                    <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => changePaymentStatus('odendi')} data-testid="statement-pay-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Ödeme Alındı İşaretle
                    </Button>
                  </>
                )}
                <div className="flex items-center justify-between pt-1 border-t">
                  <span className="text-xs text-slate-600 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Fatura {selectedRes.invoice_issued ? 'kesildi' : 'kesilmedi'}</span>
                  <Button size="sm" variant="outline" disabled={busy} onClick={toggleInvoice} data-testid="statement-invoice-btn">
                    {selectedRes.invoice_issued ? 'Faturayı Geri Al' : 'Fatura Kesildi'}
                  </Button>
                </div>
              </div>

              {/* Giderler */}
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Receipt className="w-4 h-4 text-rose-600" /> Ekstra Giderler</p>
                {(selectedRes.expenses || []).length === 0 ? (
                  <p className="text-xs text-slate-400">Gider kaydı yok</p>
                ) : (
                  <div className="divide-y">
                    {selectedRes.expenses.map(e => (
                      <div key={e.id} className="flex items-center justify-between py-1.5 text-xs" data-testid={`statement-expense-${e.id}`}>
                        <div>
                          <p className="font-medium text-slate-700">{e.description}</p>
                          <p className="text-[10px] text-slate-400">{formatDate(e.date)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-rose-600">{formatTL(e.amount)}</span>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={busy} onClick={() => deleteExpense(e.id)} data-testid={`statement-delete-expense-${e.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <form onSubmit={addExpense} className="grid grid-cols-12 gap-1.5 pt-1">
                  <Input
                    className="col-span-4 h-9 text-xs"
                    type="number"
                    step="0.01"
                    placeholder="Tutar"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    data-testid="statement-expense-amount"
                  />
                  <Input
                    className="col-span-5 h-9 text-xs"
                    placeholder="Açıklama"
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    data-testid="statement-expense-desc"
                  />
                  <Button type="submit" size="sm" className="col-span-3 h-9 bg-rose-600 hover:bg-rose-700 text-xs" disabled={busy} data-testid="statement-add-expense-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ekle
                  </Button>
                </form>
                {selectedRes.with_referee && (
                  <p className="text-[10px] text-slate-400">Hakemli maçta gider kasaya ayrıca yazılmaz; hakemin teslim edeceği netten düşülür.</p>
                )}
              </div>
            </>
          ) : (
            <DialogHeader>
              <DialogTitle className="text-base">Rezervasyon İşlemleri</DialogTitle>
            </DialogHeader>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Kısmi Tahsilat ===== */}
      <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
        <DialogContent className="max-w-md" data-testid="customer-collect-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-600" /> Tahsilat Al</DialogTitle>
            <DialogDescription className="text-xs">
              {c?.name} — güncel bakiye {formatTL(kpi?.balance || 0)}. Tutar kasaya gelir olarak yazılır ve cariden düşülür.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCollect} className="space-y-3">
            <div>
              <Label className="text-xs">Tutar (₺) *</Label>
              <Input type="number" step="0.01" value={collectForm.amount} onChange={(e) => setCollectForm({ ...collectForm, amount: e.target.value })} required data-testid="collect-amount" />
            </div>
            <div>
              <Label className="text-xs">Hangi Kasaya?</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {ACCOUNTS.map(a => (
                  <Button key={a.id} type="button" size="sm" variant={collectForm.payment_method === a.id ? 'default' : 'outline'} className="text-xs"
                    onClick={() => setCollectForm({ ...collectForm, payment_method: a.id })} data-testid={`collect-account-${a.id}`}>{a.label}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Tarih</Label>
              <Input type="date" value={collectForm.date} onChange={(e) => setCollectForm({ ...collectForm, date: e.target.value })} data-testid="collect-date" />
            </div>
            <div>
              <Label className="text-xs">Not</Label>
              <Input value={collectForm.notes} onChange={(e) => setCollectForm({ ...collectForm, notes: e.target.value })} placeholder="opsiyonel" data-testid="collect-notes" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setCollectOpen(false)}>İptal</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="submit-collect-payment">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tahsilatı İşle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerStatementModal;
